import type { GameStateManager } from '../../game/GameState';
import type { GameData, LocationId, Researcher } from '../../game/types';

interface ResearcherInterpolationState {
  lastProgress: number;
  lastTimestamp: number;
  movementCost: number;
  startX?: number;
  startY?: number;
}

export class ResearcherTransitManager {
  private interpolationStates: Map<string, ResearcherInterpolationState> = new Map();

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onResearcherSelect: (id: string) => void
  ) {}

  update(currentTime: number): void {
    const gameState = this.gameState.getState();
    if (!gameState) {
      return;
    }

    // Get cells-container for absolute positioning
    const cellsContainer = this.container.querySelector('#cells-container') as HTMLElement;
    if (!cellsContainer) {
      return;
    }

    // Show researchers that are actively in transit (targetLocation is set)
    // Also include researchers that just completed a step (were in transit but targetLocation might be null briefly)
    // OR researchers that have a movementPath (multi-step movement in progress)
    // This prevents the one-tick disappearance gap
    const researchersInTransit = gameState.researchers.filter(r => {
      if (!r.location) {
        return false;
      }
      
      // Actively moving to a target
      if (r.targetLocation !== null) {
        return true;
      }
      
      // Check if they have a movementPath (multi-step movement)
      const movementPath = r.movementPath;
      if (movementPath && movementPath.length > 0) {
        return true; // Still have more steps to go
      }
      
      // Just completed - keep showing if they were in transit (prevents one-tick gap)
      if (this.interpolationStates.has(r.id)) {
        return true;
      }
      
      return false;
    });

    // Track which researchers we're currently displaying
    const displayedResearcherIds = new Set<string>();

    // Add researchers in transit with constant-speed linear interpolation
    researchersInTransit.forEach(researcher => {
      if (!researcher.location) {
        return;
      }
      
      // Determine if actively moving, has more steps, or just completed
      const isActivelyMoving = researcher.targetLocation !== null;
      const movementPath = researcher.movementPath;
      const hasMoreSteps = movementPath && movementPath.length > 0;
      
      // Determine target location
      let targetLocation: LocationId;
      if (isActivelyMoving) {
        targetLocation = researcher.targetLocation!;
      } else if (hasMoreSteps) {
        // Next step in path
        targetLocation = movementPath![0];
      } else {
        // Just completed - use current location
        targetLocation = researcher.location;
      }

      // Find source and target cell elements
      const sourceCell = this.container.querySelector(`#cell-${researcher.location}`) as HTMLElement;
      const targetCell = this.container.querySelector(`#cell-${targetLocation}`) as HTMLElement;
      
      if (!sourceCell || !targetCell) {
        return;
      }

      // Find corridor - needed if actively moving or has more steps
      let corridor: HTMLElement | null = null;
      if (isActivelyMoving || hasMoreSteps) {
        const fromLocation = researcher.location;
        const toLocation = isActivelyMoving ? researcher.targetLocation! : targetLocation;
        
        corridor = this.container.querySelector(
          `.corridor[data-from="${fromLocation}"][data-to="${toLocation}"]`
        ) as HTMLElement;
        
        if (!corridor) {
          corridor = this.container.querySelector(
            `.corridor[data-from="${toLocation}"][data-to="${fromLocation}"]`
          ) as HTMLElement;
        }

        if (!corridor) {
          return;
        }
      }

      const initials = researcher.data.name.split(' ').map(n => n[0]).join('').substring(0, 2);
      
      // Get progress - use actual game state progress directly for constant speed
      let normalizedProgress: number;
      if (isActivelyMoving) {
        normalizedProgress = Math.max(0, Math.min(1.0, researcher.movementProgress));
      } else if (hasMoreSteps) {
        // Between steps in multi-step path - MovementSystem should set targetLocation immediately
        // But if it hasn't yet, show at current location (progress = 0) until next step starts
        // This should be very brief - just one frame
        normalizedProgress = 0;
      } else {
        // Just completed final step - show at target (progress = 1.0)
        normalizedProgress = 1.0;
      }

      // Get bounding boxes relative to cells-container
      const containerRect = cellsContainer.getBoundingClientRect();
      const sourceRect = sourceCell.getBoundingClientRect();
      const targetRect = targetCell.getBoundingClientRect();

      // Get or store starting position - CRITICAL to prevent teleport
      let interpolationState = this.interpolationStates.get(researcher.id);
      let sourceX: number, sourceY: number;
      
      if (!interpolationState || interpolationState.startX === undefined || interpolationState.startY === undefined || normalizedProgress === 0) {
        // First time or at start - capture actual position from source cell
        const sourceResearcherAvatar = sourceCell.querySelector(`.researcher-avatar[data-researcher-id="${researcher.id}"]`) as HTMLElement;
        
        if (sourceResearcherAvatar) {
          // Use ACTUAL position from source cell
          const avatarRect = sourceResearcherAvatar.getBoundingClientRect();
          sourceX = avatarRect.left + avatarRect.width / 2 - containerRect.left;
          sourceY = avatarRect.top + avatarRect.height / 2 - containerRect.top;
        } else {
          // Avatar not found - calculate based on researcher index in source cell
          const researchersInSource = gameState.researchers.filter(r => 
            r.location === researcher.location && r.targetLocation === null
          );
          const researcherIndex = researchersInSource.findIndex(r => r.id === researcher.id);
          const idx = researcherIndex >= 0 ? researcherIndex : researchersInSource.length;
          
          // Use same calculation as CellDisplay
          const leftOffset = researcher.location === 'control_room' 
            ? 20 + idx * 50 
            : 15 + (idx % 2) * 60;
          const avatarDims = this.gameData.uiConstants.avatar_dimensions;
          sourceX = sourceRect.left + leftOffset + avatarDims.offset_x - containerRect.left;
          sourceY = sourceRect.bottom - avatarDims.offset_y - containerRect.top;
        }
        
        // Store starting position
        if (!interpolationState) {
          interpolationState = {
            lastProgress: normalizedProgress,
            lastTimestamp: currentTime,
            movementCost: 1,
            startX: sourceX,
            startY: sourceY
          };
        } else {
          interpolationState.startX = sourceX;
          interpolationState.startY = sourceY;
        }
        this.interpolationStates.set(researcher.id, interpolationState);
      } else {
        // Use stored starting position
        sourceX = interpolationState.startX!;
        sourceY = interpolationState.startY!;
      }

      // Target position - where researcher will appear in target cell
      // Calculate based on how many researchers will be in target cell
      const researchersInTarget = gameState.researchers.filter(r => 
        r.location === targetLocation && r.targetLocation === null && r.id !== researcher.id
      );
      const targetIdx = researchersInTarget.length;
      const targetLeftOffset = targetLocation === 'control_room' 
        ? 20 + targetIdx * 50 
        : 15 + (targetIdx % 2) * 60;
      const avatarDims = this.gameData.uiConstants.avatar_dimensions;
      const targetX = targetRect.left + targetLeftOffset + avatarDims.offset_x - containerRect.left;
      const targetY = targetRect.bottom - avatarDims.offset_y - containerRect.top;

      // Calculate position based on progress
      let posX: number, posY: number;
      
      if (corridor && isActivelyMoving && normalizedProgress < 1.0) {
        // Get corridor center for path calculation
        const corridorRect = corridor.getBoundingClientRect();
        const corridorCenterX = corridorRect.left + corridorRect.width / 2 - containerRect.left;
        const corridorCenterY = corridorRect.top + corridorRect.height / 2 - containerRect.top;
        
        // Linear interpolation: source → corridor → target
        // Split at movement_interpolation_split for constant speed throughout the entire path
        const splitPoint = this.gameData.uiConstants.movement_interpolation_split;
        if (normalizedProgress < splitPoint) {
          // First half: source position → corridor center
          const t = normalizedProgress / splitPoint; // 0 to 1
          posX = sourceX + (corridorCenterX - sourceX) * t;
          posY = sourceY + (corridorCenterY - sourceY) * t;
        } else {
          // Second half: corridor center → target position
          const t = (normalizedProgress - splitPoint) / (1 - splitPoint); // 0 to 1
          posX = corridorCenterX + (targetX - corridorCenterX) * t;
          posY = corridorCenterY + (targetY - corridorCenterY) * t;
        }
      } else {
        // Movement completed or at target - show at target position
        posX = targetX;
        posY = targetY;
      }

      // Check if avatar already exists
      let avatar = cellsContainer.querySelector(`.researcher-avatar-transit[data-researcher-id="${researcher.id}"]`) as HTMLElement;
      
      if (!avatar) {
        avatar = document.createElement('div');
        avatar.className = 'researcher-avatar-transit';
        avatar.setAttribute('data-researcher-id', researcher.id);
        avatar.setAttribute('title', researcher.data.name);
        avatar.style.position = 'absolute';
        avatar.textContent = initials;
        avatar.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.onResearcherSelect(researcher.id);
        });
        avatar.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.onResearcherSelect(researcher.id);
        });
        cellsContainer.appendChild(avatar);
      }

      displayedResearcherIds.add(researcher.id);

      // Update position - center the avatar on the calculated position
      avatar.style.left = `${posX}px`;
      avatar.style.top = `${posY}px`;
      avatar.style.transform = 'translate(-50%, -50%)';
      
      // Add/remove moving class based on whether actually moving
      const isPaused = this.gameState.isPaused();
      if (!isPaused && normalizedProgress > 0 && normalizedProgress < 1.0) {
        avatar.classList.add('moving');
      } else {
        avatar.classList.remove('moving');
      }
      
      // Add selected class if this researcher is selected
      const selected = gameState.selectedEntity;
      const isSelected = selected.type === 'researcher' && selected.id === researcher.id;
      if (isSelected) {
        avatar.classList.add('selected');
      } else {
        avatar.classList.remove('selected');
      }
    });

    // Handle researchers that just completed final movement - keep showing until confirmed in target cell
    // Only handle those that truly completed (no movementPath)
    const justCompleted = gameState.researchers.filter(r => {
      if (r.targetLocation === null && r.location !== null) {
        // Check if they were in transit AND truly completed (no more steps)
        const movementPath = r.movementPath;
        if ((!movementPath || movementPath.length === 0) && this.interpolationStates.has(r.id)) {
          return true;
        }
      }
      return false;
    });

    justCompleted.forEach(researcher => {
      if (!researcher.location) {
        return;
      }

      // Check if researcher is now visible in target cell
      const targetCell = this.container.querySelector(`#cell-${researcher.location}`) as HTMLElement;
      if (!targetCell) {
        this.interpolationStates.delete(researcher.id);
        return;
      }

      const targetAvatar = targetCell.querySelector(`.researcher-avatar[data-researcher-id="${researcher.id}"]`) as HTMLElement;
      
      if (targetAvatar) {
        // Researcher is now visible in target cell - remove from transit
        const transitAvatar = cellsContainer.querySelector(`.researcher-avatar-transit[data-researcher-id="${researcher.id}"]`) as HTMLElement;
        if (transitAvatar) {
          transitAvatar.remove();
        }
        this.interpolationStates.delete(researcher.id);
      } else {
        // Keep showing in transit at target position until cell display updates
        const containerRect = cellsContainer.getBoundingClientRect();
        const targetRect = targetCell.getBoundingClientRect();
        const researchersInTarget = gameState.researchers.filter(r => 
          r.location === researcher.location && r.targetLocation === null && r.id !== researcher.id
        );
        const targetIdx = researchersInTarget.length;
        const targetLeftOffset = researcher.location === 'control_room' 
          ? 20 + targetIdx * 50 
          : 15 + (targetIdx % 2) * 60;
        const avatarDims = this.gameData.uiConstants.avatar_dimensions;
        const targetX = targetRect.left + targetLeftOffset + avatarDims.offset_x - containerRect.left;
        const targetY = targetRect.bottom - avatarDims.offset_y - containerRect.top;

        let avatar = cellsContainer.querySelector(`.researcher-avatar-transit[data-researcher-id="${researcher.id}"]`) as HTMLElement;
        
        if (!avatar) {
          const initials = researcher.data.name.split(' ').map(n => n[0]).join('').substring(0, 2);
          avatar = document.createElement('div');
          avatar.className = 'researcher-avatar-transit';
          avatar.setAttribute('data-researcher-id', researcher.id);
          avatar.setAttribute('title', researcher.data.name);
          avatar.style.position = 'absolute';
          avatar.textContent = initials;
          avatar.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.onResearcherSelect(researcher.id);
          });
          avatar.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.onResearcherSelect(researcher.id);
          });
          cellsContainer.appendChild(avatar);
        }

        avatar.style.left = `${targetX}px`;
        avatar.style.top = `${targetY}px`;
        avatar.style.transform = 'translate(-50%, -50%)';
        avatar.classList.remove('moving');
        
        // Add selected class if this researcher is selected
        const selected = gameState.selectedEntity;
        const isSelected = selected.type === 'researcher' && selected.id === researcher.id;
        if (isSelected) {
          avatar.classList.add('selected');
        } else {
          avatar.classList.remove('selected');
        }
        
        displayedResearcherIds.add(researcher.id);
      }
    });

    // Clean up avatars for researchers no longer in transit
    const existingAvatars = cellsContainer.querySelectorAll('.researcher-avatar-transit');
    existingAvatars.forEach(avatar => {
      const researcherId = avatar.getAttribute('data-researcher-id');
      if (researcherId && !displayedResearcherIds.has(researcherId)) {
        avatar.remove();
        this.interpolationStates.delete(researcherId);
      }
    });
  }
}

